import {RRDate} from ".";
import {RRDuration} from "../RRDuration";

type PeriodStringJSON = string;

/** Represents a period of time between two dates */
export class Period {
  /**
   * @param from - period start
   * @param to - period end
   */
  constructor(private _from: RRDate, private _to: RRDate) {}

  get from(): RRDate {
    return this._from;
  }

  get to(): RRDate {
    return this._to;
  }

  /**
   * @param other - period to check if is within this period
   * @returns true if p is within this period
   */
  public overlap(other: Period): boolean {
    return (
      (this.from.isAfterOrEqual(other.to) && this.to.isBeforeOrEqual(other.to)) ||
      (this.from.isBeforeOrEqual(other.to) && this.to.isAfterOrEqual(other.from))
    );
  }

  /**
   * @param p1 - first period
   * @param p2 - second period
   * @returns true if any from or to is overlapping between p1 and p2
   */
  public static overlap(p1: Period, p2: Period): boolean {
    return p1.from.overlap(p2) || p1._to.overlap(p2);
  }

  /**
   * @example meaning 14:00-15:00 are not inside 15:00-16:00
   * @param other - to compare against
   * @returns true if either from or to is within other doesn't handle equal by design.
   */
  public doesCollide(other: Period): boolean {
    return (this.from.isAfter(other.to) && this.to.isBefore(other.to)) || (this.from.isBefore(other.to) && this.to.isAfter(other.from));
  }

  /**
   * @param date which will be extending the boundaries of the period (unless it's within)
   * @returns this with new boundaries (from, to)
   */
  public extend(date: RRDate): Period {
    if (date.overlap(this)) return this;
    if (this.from.isAfter(date)) this._from = date;
    else this._to = date;
    return this;
  }

  /**
   * @param period - to extend or return along
   * @returns either one period if overlapping or sorted array
   */
  public add(period: Period): Period[] {
    const result: Period[] = [this];
    if (period.overlap(this)) this.addPeriodOverlaps(period);
    else result.push(period);
    return result.sort((a, b) => b.from.compare(a.from));
  }

  /**
   * @param format - the duration format for the output
   * @returns the duration
   */
  public duration(): RRDuration {
    const {hours, minutes, seconds, milliseconds} = RRDate.diff(this.to, this.from, ["hour", "minute", "second", "millisecond"]);
    return new RRDuration({hours, minutes, seconds, milliseconds});
  }

  /**
   * @param duration - to see if period covers
   * @returns true if period covers duration
   */
  public coversDuration(duration: RRDuration): boolean {
    const durationMillis = duration.toMillis();
    const periodMillis = this.to.toMillis() - this.from.toMillis();
    return periodMillis >= durationMillis;
  }

  /**
   * @param period - to see if this covers (starts before and ends after)
   * @returns true if period covers duration
   */
  public covers(period: Period): boolean {
    const thisStartsBefore = this.from.isBefore(period.from);
    const thisEndsAfter = this.to.isAfter(period.to);
    return thisStartsBefore && thisEndsAfter;
  }

  /**
   * Used to exclude a timeframe within another (period)
   * @param other - period to remove from this
   * @returns no period if other is bigger, one period if other is on the edge, or two if within
   */
  difference(other: Period): Period[] {
    const isOverlapping = this.overlap(other);
    if (!isOverlapping) return [this];

    const hasNoResult = this.from.isAfterOrEqual(other.from) && this.to.isBeforeOrEqual(other.to);
    if (hasNoResult) return [];

    const hasTwoResult = other.from.isAfter(this.from) && other.to.isBefore(this.to);
    if (hasTwoResult) return [new Period(this.from, other.from), new Period(other.to, this.to)];

    const keepBegining = this.from.isBefore(other.from);
    if (keepBegining) return [new Period(this.from, other.from)];

    const keepEnding = this.to.isAfter(other.to);
    if (keepEnding) return [new Period(other.to, this.to)];

    throw new Error("This should never happen if the above rules have not changes");
  }

  /**
   * Private helper method to extend two periods that knowingly overlap
   * @param period - to extend or return along
   */
  private addPeriodOverlaps(period: Period): void {
    this.extend(period.from);
    this.extend(period.to);
  }

  /**
   * True if requested period is within this period
   * @param period - to see if within this
   * @return true if period is within this
   */
  public intersect(period: Period): boolean {
    const compFrom = this.from.compare(period.from);
    const compTo = this.to.compare(period.to);
    return compFrom >= 0 && compTo <= 0;
  }

  /**
   * @param date - to see if within period
   * @returns true if date is between from and to of period
   */
  isWithinPeriod(date: RRDate): boolean {
    return date.isAfter(this.from) && date.isBefore(this.to);
  }

  /**
   * Returns true if this period end is before the requested periods start
   * @param period - to check if this period is before
   * @returns true if this period end is before the requested periods start
   */
  public isBefore(period: Period): boolean {
    return this.to.isBefore(period.from);
  }

  /**
   * Method that decides how two periods compare
   * @param p1 - first period
   * @param p2 - second period
   * @result -1 if p1 is before p2, 0 if equal, 1 if p1 is after p2
   */
  static compare(p1: Period, p2: Period): -1 | 0 | 1 {
    if (p1.from.isBefore(p2.from)) return -1;
    if (p1.from.isAfter(p2.from)) return 1;
    return 0;
  }

  /**
   * @param p - period to check if is within this period
   * @result -1 if this is before p, 0 if equal, 1 if this is after p
   */
  compare(p: Period): -1 | 0 | 1 {
    return Period.compare(this, p);
  }

  /**
   * Trims the given period to the maximum number of equal whole intervals that intervals start at a given minute of the period's first hour.
   * @param lengthOfIntervals minutes
   * @param startingMinuteOfFirstHour minute in an hour (0-59)
   * @returns trimmed Period
   * @example
   * new Period(new RRDate("2022-01-01 10:06:03"), new RRDate("2022-01-02 15:37:07")).trimToIntervals(12, 24) // => "2022-01-01 10:24:03 - 2022-01-02 15:36:07"
   *
   */
  trimToIntervals(lengthOfIntervals: number, startingMinuteOfFirstHour: number): Period {
    const modulusOfStart = (this._from.minute - startingMinuteOfFirstHour) % lengthOfIntervals;
    const diffOfIntervalAndPeriodStart = Math.abs(lengthOfIntervals - modulusOfStart);
    const start = new RRDate(this._from.toString("yyyy-MM-dd HH:mm:ss"), "yyyy-MM-dd HH:mm:ss").plus({minute: diffOfIntervalAndPeriodStart});
    const diffOFIntervalAndPeriodEnd = Math.floor(Math.abs(RRDate.diff(this._to, start, "minutes").minutes % lengthOfIntervals));
    const end = new RRDate(this._to.toString("yyyy-MM-dd HH:mm:ss"), "yyyy-MM-dd HH:mm:ss").minus({minute: diffOFIntervalAndPeriodEnd});
    return new Period(start, end);
  }
  /**
   * @returns a string fromated as "{from: "yyyy-MM-dd HH:mm:ss", to: "yyyy-MM-dd HH:mm:ss"}"
   */
  public toPeriodStringJSON(): PeriodStringJSON {
    return JSON.stringify({from: this._from.toString("yyyy-MM-dd HH:mm:ss"), to: this._to.toString("yyyy-MM-dd HH:mm:ss")});
  }

  /**
   *
   * @param string must have the format of "{from: "yyyy-MM-dd HH:mm:ss", to: "yyyy-MM-dd HH:mm:ss"}"
   * @returns period
   */
  public static fromPeriodStringJSON(string: PeriodStringJSON): Period {
    const obj = JSON.parse(string);
    return new Period(new RRDate(obj.from, "yyyy-MM-dd HH:mm:ss"), new RRDate(obj.to, "yyyy-MM-dd HH:mm:ss"));
  }

  /**
   * @returns a copy of this period and it's from and to dates
   */
  public clone(): Period {
    return new Period(this._from.clone(), this._to.clone());
  }
}
